import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { bulkUploadLeads } from '../../Action/api';

// The template's column headers are the exact lead field names the backend expects.
const templateHeaders = [
  'bestTimeToConnect',
  'companyName',
  'companySize',
  'companyWebsite',
  'country',
  'email',
  'facebookUrl',
  'fullName',
  'industry',
  'instagramUrl',
  'jobTitle',
  'leadId',
  'leadType',
  'linkedinUrl',
  'location',
  'numberOfEmployees',
  'otherSocialUrl',
  'phoneNumber',
  'pinterestUrl',
  'planType',
  'reasonForRequest',
  'redditUrl',
  'requestType',
  'snapchatUrl',
  'source',
  'status',
  'tiktokUrl',
  'twitterUrl',
  'whatsappUrl',
  'youtubeUrl',
];

const requiredFields = [
  'fullName',
  'email',
  'phoneNumber',
  'companyName',
  'country',
  'companySize',
  'planType',
  'source',
  'requestType',
  'bestTimeToConnect',
  'reasonForRequest',
];

// Served as-is from /public so the downloaded file always matches the backend-tested template exactly.
const TEMPLATE_URL = '/templates/orucore-leads-import-template.csv';

const downloadTemplate = () => {
  const link = document.createElement('a');
  link.href = TEMPLATE_URL;
  link.download = 'orucore-leads-import-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Minimal CSV parser that handles quoted fields, escaped quotes, and commas inside quotes.
const parseCsv = text => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\r') {
      // ignore, newline handled by \n
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows.filter(r => r.some(cell => cell.trim() !== ''));
};

const normalizeHeader = header => header.toString().trim().toLowerCase();

// Header text is the field name itself, so the lookup is just normalized-header -> field.
const headerLookup = templateHeaders.reduce((acc, field) => {
  acc[normalizeHeader(field)] = field;
  return acc;
}, {});

const readFileAsText = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the selected file.'));
    reader.onload = event => resolve(event.target.result);
    reader.readAsText(file);
  });

// The uploaded file's header row must match the template exactly (column order is not required).
const validateColumns = headerRow => {
  const normalizedExpected = templateHeaders.map(normalizeHeader);
  const normalizedUploaded = headerRow.map(normalizeHeader);

  const missing = templateHeaders.filter(label => !normalizedUploaded.includes(normalizeHeader(label)));
  const unexpected = headerRow.filter(label => !normalizedExpected.includes(normalizeHeader(label)));

  return { missing, unexpected, valid: missing.length === 0 && unexpected.length === 0 };
};

const mapRowToLead = (headerRow, cols) => {
  const lead = {};
  headerRow.forEach((rawHeader, idx) => {
    const field = headerLookup[normalizeHeader(rawHeader)];
    if (field) {
      lead[field] = (cols[idx] ?? '').trim();
    }
  });
  return lead;
};

const validateLead = lead => requiredFields.filter(field => !lead[field]);

const STATUS = {
  IDLE: 'idle',
  PARSED: 'parsed',
  IMPORTING: 'importing',
  DONE: 'done',
};

const BulkImportLeadsDialog = ({ open, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [columnError, setColumnError] = useState(null);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [uploadError, setUploadError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const resetState = () => {
    setSelectedFile(null);
    setFileName('');
    setRows([]);
    setParseError('');
    setColumnError(null);
    setStatus(STATUS.IDLE);
    setUploadError('');
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (status === STATUS.IMPORTING) return;
    resetState();
    onClose?.();
  };

  const handleFileSelect = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    setParseError('');
    setColumnError(null);
    setRows([]);
    setSelectedFile(file);
    setFileName(file.name);

    try {
      const text = await readFileAsText(file);
      const allRows = parseCsv(text);

      if (!allRows.length) {
        setParseError('No header row found. Please use the provided template.');
        setStatus(STATUS.IDLE);
        return;
      }

      const [headerRow, ...dataRows] = allRows;

      const columnCheck = validateColumns(headerRow);
      if (!columnCheck.valid) {
        setColumnError(columnCheck);
        setStatus(STATUS.IDLE);
        return;
      }

      if (!dataRows.length) {
        setParseError('No rows found in this file.');
        setStatus(STATUS.IDLE);
        return;
      }

      const mapped = dataRows.map((cols, index) => {
        const lead = mapRowToLead(headerRow, cols);
        const missing = validateLead(lead);
        return { index, lead, missing, valid: missing.length === 0 };
      });

      setRows(mapped);
      setStatus(STATUS.PARSED);
    } catch (err) {
      setParseError(err.message || 'Failed to read the file.');
      setRows([]);
      setStatus(STATUS.IDLE);
    }
  };

  const validRows = rows.filter(r => r.valid);
  const invalidRows = rows.filter(r => !r.valid);

  const handleImport = async () => {
    setStatus(STATUS.IMPORTING);
    setUploadError('');
    setUploadResult(null);

    try {
      const data = await bulkUploadLeads(selectedFile);
      setUploadResult(data || {});
      onSuccess?.();
    } catch (err) {
      setUploadError(err?.message || 'Bulk upload failed.');
    } finally {
      setStatus(STATUS.DONE);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
          Bulk Import Leads
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
          Download the sample CSV, fill it with your leads, then upload it.
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            mb: 2.5,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontWeight: 700, color: '#1e293b' }}>1. Get the sample CSV</Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Includes the required columns and two example rows.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<CloudDownloadOutlinedIcon />}
            onClick={downloadTemplate}
            sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            Download Sample CSV
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontWeight: 700, color: '#1e293b' }}>2. Upload your CSV file</Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Accepted format: .csv
            </Typography>
          </Box>
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadOutlinedIcon />}
            disabled={status === STATUS.IMPORTING}
            sx={{
              bgcolor: '#4B5AB6',
              '&:hover': { bgcolor: '#3948a0' },
              textTransform: 'none',
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            Choose CSV File
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".csv,text/csv"
              onChange={handleFileSelect}
            />
          </Button>
          {fileName && (
            <Chip
              icon={<InsertDriveFileOutlinedIcon />}
              label={fileName}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        {parseError && <Alert severity="error" sx={{ mb: 2 }}>{parseError}</Alert>}

        {columnError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              The file's columns don't match the sample CSV. Please re-download it and don't rename, remove, or add columns.
            </Typography>
            {columnError.missing.length > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Missing columns: {columnError.missing.join(', ')}
              </Typography>
            )}
            {columnError.unexpected.length > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Unexpected columns: {columnError.unexpected.join(', ')}
              </Typography>
            )}
          </Alert>
        )}

        {status === STATUS.PARSED && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Chip label={`${rows.length} rows found`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
              <Chip label={`${validRows.length} ready to import`} size="small" color="success" sx={{ fontWeight: 600 }} />
              {invalidRows.length > 0 && (
                <Chip label={`${invalidRows.length} need fixes`} size="small" color="error" sx={{ fontWeight: 600 }} />
              )}
            </Box>
            <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.index}>
                      <TableCell>{row.index + 2}</TableCell>
                      <TableCell>{row.lead.fullName || '-'}</TableCell>
                      <TableCell>{row.lead.email || '-'}</TableCell>
                      <TableCell>
                        {row.valid ? (
                          <Chip
                            size="small"
                            icon={<CheckCircleOutlineIcon sx={{ fontSize: '1rem' }} />}
                            label="Ready"
                            color="success"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            size="small"
                            icon={<ErrorOutlineIcon sx={{ fontSize: '1rem' }} />}
                            label={`Missing: ${row.missing.join(', ')}`}
                            color="error"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}

        {status === STATUS.IMPORTING && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Uploading file and processing leads on the server...
            </Typography>
            <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
          </Box>
        )}

        {status === STATUS.DONE && (
          <Box sx={{ mt: 2 }}>
            {uploadError ? (
              <Alert severity="error">{uploadError}</Alert>
            ) : (
              <>
                <Alert severity="success">
                  {uploadResult?.message || 'File uploaded successfully. The server is creating your leads.'}
                </Alert>
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(uploadResult?.insertedCount ?? uploadResult?.successCount) !== undefined && (
                    <Chip
                      label={`${uploadResult.insertedCount ?? uploadResult.successCount} created`}
                      size="small"
                      color="success"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {Array.isArray(uploadResult?.failedRows) && uploadResult.failedRows.length > 0 && (
                    <Chip
                      label={`${uploadResult.failedRows.length} failed`}
                      size="small"
                      color="error"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                {Array.isArray(uploadResult?.failedRows) && uploadResult.failedRows.length > 0 && (
                  <Box sx={{ mt: 1.5, maxHeight: 160, overflowY: 'auto' }}>
                    {uploadResult.failedRows.map((failure, i) => (
                      <Alert key={i} severity="error" sx={{ mb: 0.75 }}>
                        {failure.row ? `Row ${failure.row}: ` : ''}{failure.reason || failure.message || 'Failed to create lead'}
                      </Alert>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(148, 163, 184, 0.18)' }}>
        <Button onClick={handleClose} disabled={status === STATUS.IMPORTING} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>
          {status === STATUS.DONE ? 'Close' : 'Cancel'}
        </Button>
        {status === STATUS.DONE && uploadError && (
          <>
            <Button
              onClick={resetState}
              sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}
            >
              Clear &amp; Choose New File
            </Button>
            <Button
              variant="contained"
              onClick={handleImport}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4B5AB6 0%, #6374d9 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #3948a0 0%, #5567c8 100%)' },
              }}
            >
              Retry Upload
            </Button>
          </>
        )}
        {status === STATUS.PARSED && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={validRows.length === 0}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4B5AB6 0%, #6374d9 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #3948a0 0%, #5567c8 100%)' },
            }}
          >
            Upload {validRows.length} Lead{validRows.length === 1 ? '' : 's'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportLeadsDialog;
